import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, HelpCircle, Info, Gauge, Cable, Loader2 } from 'lucide-react';
import { computeCapacity, capacityNeedsCocon, summarizeFacadeCocon } from '@uniticket/shared';
import type { MduApartmentDto, CapacityUnit, CapacityResult, FacadeCoconSummary } from '@uniticket/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FacadeCoconState } from '@/hooks/useAddressValidation';
import { FacadeBoxModal } from './FacadeBoxModal';

// Capacité fibre — une pastille par ADRESSE dans le tableau MDU, et une modale quand on la clique.
//
// 📍 PAR ADRESSE, JAMAIS PAR BÂTIMENT. Un `belmap_building_id` groupe plusieurs adresses postales, et
// chacune a son cadran donc sa règle. Vu en production le 04-09 sur le jvid 11815065 : « Rue Panier
// Jules 2 2A » est sur un POTEAU et « 2 2D » sur une FAÇADE BOX ; la première version agrégeait les cinq
// unités et affichait un seul nombre. L'appelant passe donc un groupe de `groupApartmentsByAddress`,
// jamais `mduApartments` entier.
//
// LES DONNÉES : `MduApartmentDto.attributes` porte le cadran, le code de livraison et les scopelists ; pour
// une façade (QD / QDX), la place réelle du boîtier vient de COCON (C0.1, 17-09), lue UNE fois par la page
// (`useFacadeCocon`) et passée ici — jamais relue par la pastille. Chemin dégradé juste par construction :
// DMP injoignable → instantané, attributs nuls → « on ne sait pas » ; COCON illisible → « on ne sait pas ».
// Jamais un chiffre fabriqué.
//
// ⛔ CE NOMBRE BLOQUE. Depuis N3 (10-09), l'ajout de boîte est refusé à un non-admin quand il reste 0 — et
// depuis C0.1, un boîtier que COCON montre plein, un poteau sans client connecté et un QDX à l'arrière
// valent 0. Le formulaire d'ajout calcule avec la MÊME fonction et les MÊMES données que la pastille.

/** Les unités d'une adresse, dans la forme que la règle attend. Exporté : l'avertissement au moment du
 *  geste (ajout de boîte) doit calculer EXACTEMENT la même chose que la pastille. */
export function capacityUnitsFromApartments(apartments: MduApartmentDto[]): CapacityUnit[] {
  return apartments.map((a) => ({
    // Une ligne synthétique (boîte déclarée par un opérateur, absente du DMP) n'a pas de fibre à chercher.
    jvid: a.added ? null : a.jvid,
    quadrant: a.attributes?.quadrant ?? null,
    deliveryStatus: a.attributes?.deliveryStatus ?? null,
    scopelistV2: a.attributes?.scopelistV2 ?? null,
    scopelistV15: a.attributes?.scopelistV15 ?? null,
    outOfScopeAt: a.attributes?.outOfScopeAt ?? null,
    // L'adresse complète : c'est elle qui permet à la règle de détecter qu'on lui a passé deux adresses.
    streetName: a.streetName,
    houseNumber: a.houseNumber,
    houseNumberExtension: a.houseNumberExtension,
  }));
}

/** Le boîtier façade à donner à la règle, et si la lecture COCON est encore en cours. `pending` : ne rien
 *  afficher comme calculé (ni vert ni rouge) tant que COCON n'a pas répondu. Hors QD / QDX : rien à lire. */
export function facadeInputFor(
  units: CapacityUnit[], cocon?: FacadeCoconState,
): { facade: FacadeCoconSummary | 'unavailable' | undefined; pending: boolean } {
  if (!capacityNeedsCocon(units)) return { facade: undefined, pending: false };
  if (cocon?.status === 'ready' && cocon.data) return { facade: summarizeFacadeCocon(cocon.data, units), pending: false };
  if (cocon?.status === 'loading') return { facade: undefined, pending: true };
  return { facade: 'unavailable', pending: false };
}

const TONES = {
  ok: {
    pill: 'border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
    box: 'border-emerald-200 bg-emerald-50', text: 'text-emerald-800', muted: 'text-emerald-700',
    Icon: CheckCircle2,
  },
  build: {
    pill: 'border-rose-300 bg-rose-100 text-rose-700 hover:bg-rose-200',
    box: 'border-rose-200 bg-rose-50', text: 'text-rose-800', muted: 'text-rose-700',
    Icon: XCircle,
  },
  // Gris, jamais vert : le pire résultat possible serait un « je ne sais pas » qui se lit « c'est bon ».
  unknown: {
    pill: 'border-slate-300 bg-slate-100 text-slate-600 hover:bg-slate-200',
    box: 'border-slate-200 bg-slate-50', text: 'text-slate-700', muted: 'text-slate-500',
    Icon: HelpCircle,
  },
} as const;

/** La ligne d'arithmétique. Elle n'est pas décorative : montrer le calcul est ce qui permet de le
 *  contester. L'erreur du 03-09 (« c'est dans les 20 %, je clôture », ticket revenu une semaine après)
 *  vient d'une règle appliquée de mémoire, sur une adresse dont les ajouts étaient déjà consommés. */
function arithmetic(r: CapacityResult, t: (k: string, o?: Record<string, unknown>) => string): string {
  const rule = t(`tickets:validation.capacity.rules.${r.rule}`);
  if (r.rule === 'poleBuild') return t('tickets:validation.capacity.calcPoleBuild');
  if (r.rule === 'qdxRear') return t('tickets:validation.capacity.calcQdxRear');
  if (r.rule === 'facadeCocon' && r.facade) {
    return t('tickets:validation.capacity.calcCocon', {
      boxes: r.facade.boxes.length, ports: r.facade.ports, connected: r.facade.connected,
      planned: r.facade.planned.length, rule,
    });
  }
  if (r.cap == null || r.capKind == null) {
    return t('tickets:validation.capacity.calcNone', { built: r.built, added: r.added });
  }
  return r.capKind === 'additions'
    ? t('tickets:validation.capacity.calcAdditions', { built: r.built, added: r.added, cap: r.cap, rule })
    : t('tickets:validation.capacity.calcTotal',
      { used: r.built + r.added, cap: r.cap, built: r.built, added: r.added, rule });
}

function headline(r: CapacityResult, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (r.verdict === 'unknown') return t('tickets:validation.capacity.verdictUnknown');
  const n = r.available ?? 0;
  if (r.verdict === 'ok') return t('tickets:validation.capacity.verdictOk', { n });
  // « La demande ENTIÈRE part chez Build » ne veut rien dire quand il n'y a pas de demande. Depuis que
  // le réglage « unités demandées » a disparu de l'écran, la pastille et sa modale jugent la capacité
  // restante et rien d'autre : à zéro, on dit que l'adresse est pleine. La phrase de routage est gardée
  // pour l'ajout de boîte, où une demande existe vraiment.
  return r.requested == null
    ? t('tickets:validation.capacity.verdictFull')
    : t('tickets:validation.capacity.verdictBuild', { n });
}

/**
 * La pastille, posée au-dessus de sa propre adresse. Elle ne porte QUE le nombre — demande du user le
 * 04-09, le bloc précédent noyait le chiffre sous ses réserves. Ce sont donc la COULEUR et l'icône qui
 * portent l'alerte, et le détail vit derrière le clic.
 */
export function CapacityPill({
  apartments, cocon, onOpen,
}: { apartments: MduApartmentDto[]; cocon?: FacadeCoconState; onOpen: () => void }) {
  const { t } = useTranslation(['tickets']);
  const units = capacityUnitsFromApartments(apartments);
  const { facade, pending } = facadeInputFor(units, cocon);
  const r = computeCapacity({ units, facade });
  // Pendant la lecture COCON : ni vert ni rouge — le gris des « on ne sait pas », avec un sablier.
  const tone = TONES[pending ? 'unknown' : r.verdict];
  const n = pending || r.available == null ? '—' : String(r.available);
  const Icon = pending ? Loader2 : tone.Icon;

  return (
    <button
      type="button"
      // La ligne d'en-tête n'est pas cliquable aujourd'hui, mais ses voisines le sont
      // (`<tr role="button">`) : on verrouille plutôt que de compter sur le voisinage.
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      aria-label={pending ? t('tickets:validation.capacity.pillLoading') : t('tickets:validation.capacity.pillLabel', { n })}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5',
        'text-[10px] font-bold transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose/40',
        tone.pill,
      )}
    >
      <Icon className={cn('h-3 w-3 shrink-0', pending && 'animate-spin')} />
      {t('tickets:validation.capacity.pillText', { n })}
    </button>
  );
}

/**
 * Le verdict et ce qu'il faut en penser : la phrase, l'arithmétique, le cadran et sa source, les réserves. C'est le
 * corps de la modale de détail, sorti tel quel (C0.5, 08-10) pour que le formulaire d'ajout de la console DMP — qui
 * n'a ni tableau ni pastille — montre EXACTEMENT ce que la fenêtre de validation montre. Un seul rendu, pour la
 * même raison qu'un seul calcul.
 */
export function CapacitySummary({ result: r, pending }: { result: CapacityResult; pending: boolean }) {
  const { t } = useTranslation(['tickets']);
  const tone = TONES[pending ? 'unknown' : r.verdict];
  return (
    <>
      {pending ? (
        <div className={cn('rounded-lg border p-3', tone.box)}>
          <p className={cn('flex items-center gap-1.5 text-sm font-semibold', tone.text)}>
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            {t('tickets:validation.capacity.loadingCocon')}
          </p>
        </div>
      ) : (
        <div className={cn('rounded-lg border p-3', tone.box)}>
          <p className={cn('flex items-center gap-1.5 text-sm font-semibold', tone.text)}>
            <tone.Icon className="h-4 w-4 shrink-0" />
            {headline(r, t)}
          </p>
          <p className={cn('mt-1 text-[11px] leading-snug', tone.muted)}>{arithmetic(r, t)}</p>
          {r.over && (
            <p className={cn('text-[11px] font-medium', tone.text)}>
              {r.facade
                ? t('tickets:validation.capacity.facade.missing', { count: r.facade.missing })
                : t('tickets:validation.capacity.over')}
            </p>
          )}
        </div>
      )}

      {r.quadrant && (
        <p className="mt-3 text-[11px] text-slate-500">
          {t('tickets:validation.capacity.quadrant', { quadrant: r.quadrant })}
          {' — '}
          {t(`tickets:validation.capacity.source.${r.quadrantSource ?? 'delivery'}`)}
        </p>
      )}

      {!pending && r.reserves.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {r.reserves.map((id) => (
            <li key={id} className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-500">
              <Info className="mt-px h-3 w-3 shrink-0" />
              <span>{t(`tickets:validation.capacity.reserves.${id}`)}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * Le détail, en modale. Portalisée à dessein : la pastille vit dans un `<td>`, un overlay non portalisé
 * serait rendu à l'intérieur du tableau. Même motif que les modales de la console DMP (clic dehors et
 * Échap ferment).
 */
export function CapacityDetailModal({
  apartments, addressLabel, cocon, onClose,
}: { apartments: MduApartmentDto[]; addressLabel: string; cocon?: FacadeCoconState; onClose: () => void }) {
  const { t } = useTranslation(['tickets']);
  const units = capacityUnitsFromApartments(apartments);
  const { facade, pending } = facadeInputFor(units, cocon);
  const r = computeCapacity({ units, facade });
  // N23 — le détail COCON s'ouvre PAR-DESSUS cette modale, qui reste montée : le fermer ramène au calcul.
  const [showBoxes, setShowBoxes] = useState(false);

  useEffect(() => {
    // Tant que le détail COCON est ouvert, Échap est à lui : une pression ferme une couche, pas les deux.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !showBoxes) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, showBoxes]);

  const modal = createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('tickets:validation.capacity.title')}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl"
      >
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <Gauge className="h-4 w-4 text-brand-rose" />
            {t('tickets:validation.capacity.title')}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">{addressLabel}</p>
        </div>

        <div className="px-5 py-4">
          {/* La phrase qui explique pourquoi il peut y avoir DEUX nombres sur un même bâtiment. Sans
              elle, deux pastilles côte à côte passent pour une incohérence de l'app. */}
          <p className="mb-3 text-[11px] leading-snug text-slate-500">
            {t('tickets:validation.capacity.perAddress')}
          </p>
          <CapacitySummary result={r} pending={pending} />

          <div className="mt-4 flex items-end justify-between gap-3">
            <p className="text-[10px] italic leading-snug text-slate-400">
              {t('tickets:validation.capacity.informative')}
            </p>
            <Button size="sm" variant="outline" className="shrink-0" onClick={() => setShowBoxes(true)}>
              <Cable className="mr-1.5 h-4 w-4" />
              {t('tickets:validation.capacity.facade.more')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );

  // Le détail COCON est rendu À CÔTÉ de ce portail, pas dedans : les événements React remontent l'arbre des
  // composants même à travers un portail, et un clic dans le détail fermerait sinon cette modale-ci aussi.
  // Pour une adresse QD / QDX il reçoit la lecture de la page : il montre alors EXACTEMENT le calcul ci-dessus.
  return (
    <>
      {modal}
      {showBoxes && (
        <FacadeBoxModal
          apartments={apartments}
          addressLabel={addressLabel}
          cocon={capacityNeedsCocon(units) ? cocon : undefined}
          onClose={() => setShowBoxes(false)}
        />
      )}
    </>
  );
}
